import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendSms, sendEmailCode, submitSmsCode, loginWithTaskId, saveTokens } from '../../../api/submitSmsCode';
import logo from '../../../app_icon.svg';
import Modal from '../../../common/components/MessageModal';
import { t } from 'i18next';
import monitoringService from '../../../services/monitoringService';

const SmsCode = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { taskId } = location.state || {};
    const { phoneNumber } = location.state || {};
    const { typeAuth } = location.state || {};
    const { email } = location.state || {};
    const { hasEmailOption } = location.state || false;

    const [code, setCode] = useState(new Array(6).fill(''));
    const [timer, setTimer] = useState(30);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isEmailRequested, setIsEmailRequested] = useState(false);
    const [isRequestingEmail, setIsRequestingEmail] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (timer > 0) {
            const countdown = setTimeout(() => setTimer(timer - 1), 1000);
            return () => clearTimeout(countdown);
        }
    }, [timer]);

    useEffect(() => {
        const accessToken = localStorage.getItem('access_token');
        if (accessToken) {
            navigate('/profile#walletTopUp');
        } else {
            sendSms(taskId).catch((err) => {
                setError(err.message);
                setShowModal(true);
            });
        }
    }, [navigate, taskId]);

    const handleBackClick = () => {
        navigate('/sign');
    };

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return;
        const newCode = [...code];
        newCode[index] = element.value;
        setCode(newCode);

        if (element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !code[index] && e.target.previousSibling) {
            e.target.previousSibling.focus();
        }
    };

    const handleSmsSubmit = async (e) => {
        e.preventDefault();
        const smsCode = code.join('');

        setIsLoading(true);
        try {
            const task_id_from_response1 = await submitSmsCode(smsCode, taskId, phoneNumber);
            const loginData = await loginWithTaskId(task_id_from_response1, typeAuth);

            saveTokens(loginData.access_token, loginData.refresh_token, loginData.biometric_token);

            if (typeAuth === 'login') {
                monitoringService.trackLogin(phoneNumber, true, null).catch(() => {});
            } else if (typeAuth === 'signup') {
                monitoringService.trackRegistration(phoneNumber, true, null).catch(() => {});
            }

            if (phoneNumber) {
                monitoringService.setPhoneNumber(phoneNumber);
            }
            monitoringService.fetchUserPhoneNumber().catch(() => {});

            window.dispatchEvent(new CustomEvent('userLogin', {
                detail: { accessToken: loginData.access_token }
            }));

            navigate('/');
        } catch (err) {
            const errorMessage = err.message || 'Ошибка проверки кода.';
            if (typeAuth === 'login') {
                monitoringService.trackLogin(phoneNumber, false, errorMessage).catch(() => {});
            } else if (typeAuth === 'signup') {
                monitoringService.trackRegistration(phoneNumber, false, errorMessage).catch(() => {});
            }
            setError(errorMessage);
            setShowModal(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = () => {
        setTimer(30);
        setCode(new Array(6).fill(''));
        setIsEmailRequested(false);
        sendSms(taskId).catch((err) => {
            setError(err.message || 'Ошибка при отправке SMS.');
            setShowModal(true);
        });
    };

    const handleRequestEmailCode = async () => {
        if (!hasEmailOption || !email) {
            setError(t('emailNotAvailable') || 'Email недоступен');
            setShowModal(true);
            return;
        }

        setIsRequestingEmail(true);
        try {
            await sendEmailCode(taskId);
            setIsEmailRequested(true);
            setError(t('emailCodeSent') || 'Код отправлен на email');
            setShowModal(true);
        } catch (err) {
            setError(err.message || 'Ошибка при отправке кода на email');
            setShowModal(true);
        } finally {
            setIsRequestingEmail(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError('');
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Section - Decorative */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url('/images/sign.png')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#147F26]/95 via-[#0f6820]/90 to-[#0a4d16]/95" />

                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-64 h-64 border-2 border-white rounded-full" />
                    <div className="absolute bottom-32 right-16 w-48 h-48 border-2 border-white rounded-full" />
                    <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white rounded-full" />
                </div>

                <div className="relative z-10 flex flex-col justify-center px-16 py-12 text-white">
                    <h1 className="text-4xl font-bold mb-4 leading-tight">
                        {t('verifyTitle') || 'Растау коды'}
                    </h1>
                    <p className="text-lg text-white/80">
                        {isEmailRequested
                            ? (t('otpSentEmail') || 'Код email-ге жіберілді')
                            : (t('otpSentPhone') || 'Код телефонға жіберілді')}
                    </p>
                </div>
            </div>

            {/* Right Section - Form */}
            <div className="w-full lg:w-1/2 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 lg:px-12 lg:py-6 flex-shrink-0">
                    <button
                        onClick={handleBackClick}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#147F26] transition-colors"
                    >
                        <div className="w-10 h-10 rounded-full bg-[#147F26]/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-[#147F26]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </div>
                        <span className="text-sm font-medium hidden sm:inline">{t('back') || 'Артқа'}</span>
                    </button>

                    <img src={logo} alt="Agroland" className="h-10 lg:h-12" />
                </div>

                {/* Form Area */}
                <div className="flex-1 overflow-y-auto px-6 py-6 lg:px-16 lg:py-6 flex items-center">
                    <div className="w-full max-w-md mx-auto">
                        {/* Mobile Logo */}
                        <div className="lg:hidden flex justify-center mb-8">
                            <img src={logo} alt="Agroland" className="h-14" />
                        </div>

                        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                            {t('enter_code') || 'Кодты енгізу'}
                        </h2>

                        <p className="text-gray-500 mb-2">
                            {isEmailRequested
                                ? (t('codeSentToEmail') || 'Код email-ге жіберілді')
                                : (t('codeSentToPhone') || 'Код телефонға жіберілді')}
                        </p>

                        {email && hasEmailOption && (
                            <p className="text-sm text-gray-500 mb-6">
                                {email}
                            </p>
                        )}

                        <form onSubmit={handleSmsSubmit} className="space-y-6">
                            {/* Code Inputs */}
                            <div className="flex justify-center gap-2 lg:gap-3">
                                {code.map((value, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={value}
                                        onChange={(e) => handleChange(e.target, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onFocus={(e) => e.target.select()}
                                        className="w-11 h-14 lg:w-12 lg:h-14 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#147F26] focus:ring-2 focus:ring-[#147F26]/20 outline-none transition-all"
                                    />
                                ))}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || code.join('').length < 6}
                                className="w-full py-4 bg-[#147F26] hover:bg-[#0f6820] disabled:bg-gray-300 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#147F26]/25"
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        {t('verifying') || 'Тексерілуде...'}
                                    </>
                                ) : (
                                    <>
                                        {t('confirm') || 'Растау'}
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Resend Timer */}
                        <div className="flex items-center justify-center gap-3 mt-6">
                            <p className="text-sm text-gray-500">
                                {timer > 0
                                    ? `${t('resendIn') || 'Қайта жіберу'} ${timer}s`
                                    : (t('didNotGetCode') || 'Код келмеді пе?')}
                            </p>
                            {timer === 0 && (
                                <button
                                    onClick={handleResendCode}
                                    className="text-sm font-semibold text-[#147F26] hover:underline"
                                >
                                    {t('resendOtp') || 'Қайта жіберу'}
                                </button>
                            )}
                        </div>

                        {/* Email Option */}
                        {hasEmailOption && email && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <p className="text-sm text-gray-500 text-center mb-3">
                                    {t('preferEmail') || 'Email арқылы код алғыңыз келе ме?'}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleRequestEmailCode}
                                    disabled={isRequestingEmail}
                                    className="w-full py-3 border-2 border-gray-200 hover:border-[#147F26] hover:bg-[#147F26]/5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-gray-700 disabled:opacity-50"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    {isRequestingEmail
                                        ? (t('sending') || 'Жіберілуде...')
                                        : (t('sendToEmail') || 'Email-ге жіберу')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 text-center text-xs text-gray-400 flex-shrink-0 border-t border-gray-100">
                    {t('secureCode') || 'Кодты қауіпсіз растау арқылы'}
                </div>
            </div>

            <Modal show={showModal} message={error} onClose={handleCloseModal} />
        </div>
    );
};

export default SmsCode;
